<template>
  <config-base-comp
    :show-plan-details="showPlanDetails"
    class="opc-sdk-uc"
    @view-details="viewDetails">
    <div slot="config" class="opc-sdk-uc__config">
      <sdk-credit-group
        v-model="value"
        :category-list="creditCategoryList"
        :select-group-title="$t('billing.video_sdk_uc.select_plan_title')"
        :ax-select-group-title="title"
        :trackingPlanName="trackingPlanName"
        :unit="planUnitMap.credits"
        group-category-key="plan_code"
        @switchCategoryAction="switchCategoryAction"
      />
    </div>
    <div slot="footer" class="opc-sdk-uc__footer">
      <span class="opc-sdk-uc__footer--desc">{{ $t('billing.video_sdk_uc.switch_desc') }}</span>
      <zm-button data-testid="checkout-button-86" class="opc-sdk-uc__footer--switch" type="link" @click="handleSwitch">
        {{ $t('billing.video_sdk_uc.switch_cta') }}
      </zm-button>
    </div>
  </config-base-comp>
</template>

<script type="text/jsx">
import _ from 'lodash';
import ConfigBase from '@/components/config-base';
import mxPlanConfig from '@/plans/VIDEO_SDK_UC/mixin';
import ConfigCustomGroup from '@/components/service/config-custom-group/index';
import { plansof } from '@/utils/chain';
import { mapActions, mapGetters } from 'vuex';
import { getPlanUnitWithQuantity, planUnitMap } from '@/common/product-unit';
import SaveCount from '@/components/save-count';
import { isEmptyVal } from '@/utils';
import { ServiceConfirm } from '@/components/common/service-dialog/confirm';
import PlanDetails from '@/plans/VIDEO_SDK_UC/dialog/plan-details';
import CreditRateFeature from '@/plans/VIDEO_SDK_UC/dialog/credit-rate-feature';
import { trackingPlanTypes } from '@/common/constants';
import { getDefaultFlowType } from '@/common/service/page-config';
import UsageTypeMixins from '@/views/checkout/address/mx-usage-type';

const ConfigBaseComp = {
  name: 'sdk-uc-base',
  extends: ConfigBase,
  mixins: [mxPlanConfig],
  props: {
    showPlanDetails: Boolean,
    planDetailsLabel: String,
  },
  data() {
    return {
      showLearnMore: false,
      unit: planUnitMap.credits,
    };
  },
  methods: {
    showCreditRate() {
      this.creditRateFeatureDialog.confirm();
      this.$tracking.buyInteraction(
        'credit rates selected',
        trackingPlanTypes.VIDEO_SDK_UC,
      );
    },
    mx_renderLicense(plan) {
      if (!plan.cartInfo.selected_number) return;
      return <li class="text-lowercase">{`${plan.credit} ${getPlanUnitWithQuantity(this.unit)}`}</li>;
    },
    mx_renderConfigDesc() {
      return <div class="opc-zm-base-card__main--desc">
        <span class="opc-zm-base-card__main--desc__content" domPropsInnerHTML={this.subtitle} />
        <zm-button data-testid="checkout-button-87" type="link" onClick={this.showCreditRate} class="opc-zm-base-card__main--desc__btn">
          {this.$t('billing.video_sdk_uc.credit_rates_cta')}
        </zm-button>
      </div>;
    },
  },
  created() {
    this.creditRateFeatureDialog = new ServiceConfirm(CreditRateFeature);
  },
};

const SdkCreditGroup = {
  extends: ConfigCustomGroup,
  components: { SaveCount },
  methods: {
    mx_renderGroupCol(item = {}) {
      const { plan = {}, disabled, savedPercentValue } = item;

      return <zm-radio data-testid="checkout-radio-88" disabled={disabled} label={plan.plan_code}>
        <p class="opc-custom-group__plan__title">
          <span>{plan.credit} {getPlanUnitWithQuantity(planUnitMap.credits)}</span>
          {savedPercentValue ? <save-count
            class="opc-custom-group__plan__save"
            {...{
              props: {
                disabled,
                savedPercentValue,
              },
            }}
          /> : null}
        </p>
        <p class="opc-custom-group__plan__price">
          <span class="opc-custom-group__plan__price--amount">
            {this.$money(plan.amount, { forceDiff: true })}
          </span>
          <span class="opc-custom-group__plan__price--unit">
            {this.$t('billing.common.per_billing_cycle.monthly')}<priceVATTip />
          </span>
        </p>
      </zm-radio>;
    },
  },
};

export default {
  components: {
    ConfigBaseComp,
    SdkCreditGroup,
  },
  mixins: [mxPlanConfig, UsageTypeMixins],
  data() {
    return {
      value: '',
      planUnitMap,
    };
  },
  computed: {
    ...mapGetters(['planMapFlat', 'getSelectedPlans']),
    showPlanDetails() {
      return true;
    },
    planList() {
      return plansof(this.planMapFlat).type(this.planType).get();
    },
    creditCategoryList() {
      const length = this.planList?.length || 0;

      return _.chain(this.planList)
        .orderBy('credit')
        .map((plan, index) => {
          const isLast = index === length - 1;

          return {
            plan,
            disabled: false,
            disabledTooltip: '',
            savedPercentValue: isLast ? '10%' : undefined,
          };
        })
        .value();
    },
    selectedPlan() {
      return plansof(this.getSelectedPlans).type(this.planType).get(0);
    },
  },
  methods: {
    ...mapActions(['switchSelectPlanAction']),
    viewDetails() {
      this.planDetailsDialog.confirm({
        title: this.title,
      });

      this.$tracking.buyInteraction(
        'plan details selected',
        trackingPlanTypes.VIDEO_SDK_UC,
      );
    },
    switchCategoryAction(oldPlan, newPlan) {
      this.switchSelectPlanAction([oldPlan?.plan_code, {
        ...newPlan,
        cartInfo: {
          selected_number: 1,
        },
      }]);
      this.$tracking.buyInteraction(trackingPlanTypes.VIDEO_SDK_UC,newPlan?.credit+' credits');
    },
    handleSwitch() {
      const type = getDefaultFlowType();
      this.$tracking.buyInteraction('explore other plans', `${this.trackingPlanName} - switch now to pay as you go`);
      location.href = `/opc/buy?plan=litesdk&type=${type}`;
    },
  },
  created() {
    if (!isEmptyVal(this.selectedPlan)) {
      this.value = this.selectedPlan?.plan_code;
    }

    this.planDetailsDialog = new ServiceConfirm(PlanDetails);
  },
};
</script>

<style lang="scss">
.opc-sdk-uc {
  &__config {

  }

  .opc-zm-base-card__main__footer {
    margin-top: 32px;
  }

  &__footer {
    text-align: left;

    &--desc, &--switch {
      font-size: 14px;
      font-style: normal;
      line-height: 150%;
    }

    &--desc {
      font-weight: 400;
      color: var(--day-foreground-foreground-ada, #666487);
    }

    &--switch {
      &.zm-button.zm-button--link {
      margin-left: 2px;
      color: var(--day-semantic-sentiment-info, #0B5CFF);
      font-weight: 500;
    }
  }
}
}
</style>
